import mixpanel from 'mixpanel-browser';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { MIXPANEL_REPLAYS_TOKEN } from '@constants/config';
import { MixpanelFlagValues, MixpanelFlags } from '@constants/featureFlags';
import {
  getFlagsContextVersion,
  subscribeToFlagsContext,
} from '@tracking/mixpanelFlagsContext';

// DON'T use this hook in many components for the same flag. Each call tracks
// an "exposure" event in Mixpanel (what experiment analysis counts on), so N
// components = N exposures, and different components could briefly show
// different variants. When a flag is needed in more than one place, build a
// Provider that calls this hook
// once at the top and shares the value through React context.
// Network requests are NOT a concern: the Mixpanel SDK fetches all flag
// assignments once and reads from memory afterwards. It re-fetches when the
// evaluation context changes — i.e. when the basket changes, see
// `@tracking/mixpanelFlagsContext` — and that re-arms exposure tracking, so a
// mounted component reports a fresh exposure against the new basket.
const useMixpanelFlag = <K extends MixpanelFlags>(
  key: K,
  defaultValue: MixpanelFlagValues[K]
) => {
  const [value, setValue] = useState<MixpanelFlagValues[K]>(() =>
    MIXPANEL_REPLAYS_TOKEN
      ? (mixpanel.flags.get_variant_value_sync(
          key,
          defaultValue
        ) as MixpanelFlagValues[K])
      : defaultValue
  );
  const [loading, setLoading] = useState(!!MIXPANEL_REPLAYS_TOKEN);

  // Bumped once the SDK has installed variants for a new basket. Without it
  // this hook would keep serving the variant it resolved on mount.
  const contextVersion = useSyncExternalStore(
    subscribeToFlagsContext,
    getFlagsContextVersion,
    getFlagsContextVersion
  );

  useEffect(() => {
    if (!MIXPANEL_REPLAYS_TOKEN) return;

    let cancelled = false;

    mixpanel.flags.get_variant_value(key, defaultValue).then((variant) => {
      if (cancelled) return;
      setValue(variant as MixpanelFlagValues[K]);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [key, defaultValue, contextVersion]);

  return { value, loading };
};

export { useMixpanelFlag };
